import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, ArrowRight, XCircle } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { registerComplete } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | ready | error
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState("error"); setErr("Missing verification token."); return; }
    api.post("/auth/register/verify", { token })
      .then(({ data }) => { setInfo(data); setState("ready"); })
      .catch((e) => { setErr(formatApiError(e)); setState("error"); });
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      await registerComplete({ token, password });
      navigate("/", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center p-6" style={{ background: "linear-gradient(180deg, hsl(var(--tone-lavender)) 0%, hsl(var(--background)) 100%)" }}>
      <div className="yl-orb yl-orb-a" /><div className="yl-orb yl-orb-b" />
      <div data-testid="verify-root" className="relative w-full max-w-md rounded-3xl bg-card border border-border shadow-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: "hsl(var(--navy))" }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 2 L20 15 H13 Z" fill="hsl(var(--cyan))" /><path d="M12 2 L4 15 H11 Z" fill="#fff" fillOpacity="0.92" /><path d="M9 17 H15 L13.5 22 H10.5 Z" fill="hsl(var(--gold))" /></svg>
          </div>
          <div className="font-heading text-xl">Meet<span style={{ color: "hsl(var(--cyan))" }}>IQ</span><span style={{ color: "hsl(var(--gold))" }}>360</span></div>
        </div>

        {state === "checking" && (
          <div className="py-10 text-center">
            <Loader2 className="w-10 h-10 animate-spin mx-auto" style={{ color: "hsl(var(--cyan))" }} />
            <p className="text-sm text-muted-foreground mt-4">Verifying your email…</p>
          </div>
        )}

        {state === "error" && (
          <div className="py-6 text-center">
            <div className="w-14 h-14 rounded-full grid place-items-center mx-auto bg-rose-100"><XCircle className="w-7 h-7 text-rose-600" /></div>
            <h2 className="font-heading text-2xl mt-4" style={{ color: "hsl(var(--navy))" }}>Link not valid</h2>
            <p className="text-sm text-muted-foreground mt-2" data-testid="verify-error">{err}</p>
            <Link to="/login" data-testid="verify-error-back-link" className="mt-6 inline-flex rounded-full px-5 py-2.5 text-sm font-semibold text-white" style={{ background: "hsl(var(--navy))" }}>Back to sign up</Link>
          </div>
        )}

        {state === "ready" && (
          <>
            <div className="inline-flex items-center gap-2 rounded-full tone-powder px-3 py-1 text-[11px] font-semibold mb-3">
              <ShieldCheck className="w-3.5 h-3.5" /> Email verified
            </div>
            <h2 className="font-heading text-[32px] leading-tight tracking-tight" style={{ color: "hsl(var(--navy))" }}>Set your password</h2>
            <p className="text-sm text-muted-foreground mt-2">
              Welcome {info?.name ? info.name.split(" ")[0] : ""}! Create a password for <b className="text-foreground">{info?.email}</b> to finish setting up <b className="text-foreground">{info?.org_name}</b>.
            </p>
            <form onSubmit={submit} data-testid="verify-set-password-form" className="mt-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pw" className="eyebrow">Password</Label>
                <Input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="verify-password-input" placeholder="At least 8 characters" required
                  className="rounded-2xl h-12 px-4 bg-white border-border focus:border-primary" autoFocus />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cpw" className="eyebrow">Confirm password</Label>
                <Input id="cpw" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  data-testid="verify-confirm-input" placeholder="Re-enter password" required
                  className="rounded-2xl h-12 px-4 bg-white border-border focus:border-primary" />
              </div>
              {err && <div data-testid="verify-error" className="tone-blush rounded-2xl px-4 py-3 text-sm border border-rose-200/60">{err}</div>}
              <Button type="submit" data-testid="verify-submit-btn" disabled={busy}
                className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))] shadow-lift group">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <span className="inline-flex items-center gap-2 font-semibold">Create account & continue <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></span>
                )}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
